import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  useAdminUsers,
  useAdminPlans,
  useUserPlanSummary,
  useAssignFixedPlan,
  useAssignPaygPlan,
  useAssignEnterprisePlan,
  useUpdateExpiration,
  useDeleteUserPlan,
} from '../../hooks/useAdminUsage';
import { UserListItem } from '../../services/usage.service';
import { Target, Users, TrendingUp, Calendar, AlertCircle, Edit, Trash2, FileCheck, MessageCircle, Radar, Unlock } from 'lucide-react';
import { PlanAssignmentModal, PlanFormData } from '../ui/PlanAssignmentModal';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { SearchInput } from '../ui/search-input';
import { Input } from '@/components/ui/atoms/Input';
import { ProgressBar } from '@/components/ui/atoms/ProgressBar';
import type { ProgressBarIntent } from '@/components/ui/atoms/ProgressBar';
import { Button } from '@/components/ui/atoms/Button';
import {
  formatDaysUntilExpiration,
  formatPlanExpirationDate,
} from '@/utils/date';
import { Tag } from '../ui/atoms/Tag';

interface UsageManagementProps {
  isAdmin: boolean;
  isLoading: boolean;
}

/**
 * The billed units we surface, in display order. `get_plan_summary()` also
 * returns a `job` counter, which is deliberately not shown here: jobs are not
 * billed against the plan (see the Billing page copy).
 */
const USAGE_METRICS: {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { key: 'cv', label: 'CV Scoring', icon: FileCheck },
  { key: 'interview', label: 'AI Interviews', icon: MessageCircle },
  { key: 'talent_search', label: 'Talent Searches', icon: Radar },
  { key: 'talent_unlock', label: 'Talent Unlocks', icon: Unlock },
];

export const UsageManagement: React.FC<UsageManagementProps> = ({ isAdmin, isLoading }) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingExpiration, setEditingExpiration] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');

  // Data fetching hooks
  const { data: users = [], isLoading: isLoadingUsers, error: usersError } = useAdminUsers({
    enabled: isAdmin && !isLoading,
  });

  const { data: plans = [], error: plansError } = useAdminPlans({
    enabled: isAdmin && !isLoading,
  });

  const { data: userPlan, isLoading: isLoadingPlan, error: planError } = useUserPlanSummary(selectedUserId, {
    enabled: !!selectedUserId,
  });

  // Mutation hooks
  const assignFixedPlanMutation = useAssignFixedPlan();
  const assignPaygPlanMutation = useAssignPaygPlan();
  const assignEnterprisePlanMutation = useAssignEnterprisePlan();
  const updateExpirationMutation = useUpdateExpiration();
  const deletePlanMutation = useDeleteUserPlan();


  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return users;
    const searchLower = debouncedSearchTerm.toLowerCase();
    return users.filter(user => 
      user.email?.toLowerCase().includes(searchLower) ||
      user.name?.toLowerCase().includes(searchLower)
    );
  }, [users, debouncedSearchTerm]);

  // Get selected user object
  const selectedUser = useMemo(() => {
    return users.find(u => u.id === selectedUserId) || null;
  }, [users, selectedUserId]);

  const handleUserSelect = (user: UserListItem) => {
    setSelectedUserId(user.id);
    setShowPlanForm(false);
    setEditingExpiration(false);
  };

  const handlePlanAssignment = async (formData: PlanFormData & { userId?: string }) => {
    if (!selectedUserId) return;

    try {
      if (formData.planType === 'fixed') {
        await assignFixedPlanMutation.mutateAsync({
          userId: selectedUserId,
          request: { plan_code: formData.fixedPlanCode },
        });
        toast.success(i18n.t('common:toasts.admin.fixed_plan_assigned'));
      } else if (formData.planType === 'payg') {
        await assignPaygPlanMutation.mutateAsync({
          userId: selectedUserId,
          request: { expires_at: formData.expiresAt || undefined },
        });
        toast.success(i18n.t('common:toasts.admin.payg_plan_assigned'));
      } else if (formData.planType === 'enterprise') {
        await assignEnterprisePlanMutation.mutateAsync({
          userId: selectedUserId,
          limits: formData.limits,
          expiresAt: formData.expiresAt || undefined,
        });
        toast.success(i18n.t('common:toasts.admin.enterprise_plan_assigned'));
      }
      setShowPlanForm(false);
    } catch (err) {
      const fallback = i18n.t('common:toasts.admin.plan_save_failed');
      toast.error(err instanceof Error && err.message !== i18n.t('common:toasts.admin.plan_save_failed', { lng: 'en' }) ? err.message : fallback);
      throw err;
    }
  };

  const handleDeletePlan = () => {
    if (!selectedUserId) return;
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUserId) return;

    try {
      await deletePlanMutation.mutateAsync(selectedUserId);
      toast.success(i18n.t('common:toasts.admin.plan_deleted'));
      setShowDeleteConfirm(false);
    } catch (err) {
      const fallback = i18n.t('common:toasts.admin.plan_delete_failed');
      toast.error(err instanceof Error && err.message !== i18n.t('common:toasts.admin.plan_delete_failed', { lng: 'en' }) ? err.message : fallback);
    }
  };

  const handleEditExpiration = () => {
    if (userPlan?.expires_at) {
      const date = new Date(userPlan.expires_at);
      setExpirationDate(date.toISOString().split('T')[0]);
    } else {
      setExpirationDate('');
    }
    setEditingExpiration(true);
  };

  const handleSaveExpiration = async () => {
    if (!selectedUserId || !expirationDate) return;

    try {
      const expiresAt = `${expirationDate}T23:59:59`;
      await updateExpirationMutation.mutateAsync({
        userId: selectedUserId,
        expiresAt,
      });
      toast.success(i18n.t('common:toasts.admin.expiration_updated'));
      setEditingExpiration(false);
    } catch (err) {
      const fallback = i18n.t('common:toasts.admin.expiration_update_failed');
      toast.error(err instanceof Error && err.message !== i18n.t('common:toasts.admin.expiration_update_failed', { lng: 'en' }) ? err.message : fallback);
    }
  };

  const formatLastActivity = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    const activityDate = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - activityDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears}y ago`;
  };

  const getUsageColor = (used: number, limit: number) => {
    if (limit === -1) return 'text-blue-300';
    const percentage = (used / limit) * 100;
    if (percentage >= 90) return 'text-red-300';
    if (percentage >= 70) return 'text-purple-300';
    return 'text-blue-300';
  };

  const isSaving = assignFixedPlanMutation.isPending ||
                   assignPaygPlanMutation.isPending ||
                   assignEnterprisePlanMutation.isPending ||
                   updateExpirationMutation.isPending ||
                   deletePlanMutation.isPending;

  if (!isAdmin) {
    return (
      <div className="bg-gradient-to-r from-red/10 to-light-red/20 border border-red/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-red/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red" />
          </div>
          <div>
            <h3 className="text-sm font-heading font-bold text-dark-invirtus mb-1">Access Denied</h3>
            <p className="text-dark-invirtus/70 text-sm">Admin privileges required.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show errors from queries
  useEffect(() => {
    if (usersError) {
      toast.error(usersError instanceof Error && usersError.message !== i18n.t('common:toasts.admin.users_load_failed', { lng: 'en' }) ? usersError.message : i18n.t('common:toasts.admin.users_load_failed'));
    }
  }, [usersError]);

  useEffect(() => {
    if (planError) {
      toast.error(planError instanceof Error && planError.message !== i18n.t('common:toasts.admin.user_plan_load_failed', { lng: 'en' }) ? planError.message : i18n.t('common:toasts.admin.user_plan_load_failed'));
    }
  }, [planError]);

  useEffect(() => {
    if (plansError) {
      toast.error(plansError instanceof Error && plansError.message !== i18n.t('common:toasts.admin.plans_load_failed', { lng: 'en' }) ? plansError.message : i18n.t('common:toasts.admin.plans_load_failed'));
    }
  }, [plansError]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* User Selection */}
        <div className="bg-white rounded-xl border border-dark-invirtus/5 overflow-hidden">
          <div className="p-4 border-b border-light-lila/30 bg-gradient-to-r from-light-lila/20 to-light-purple/10">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-heading font-bold text-dark-invirtus flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple" />
                  Select User
                </h3>
                <p className="text-xs text-dark-invirtus/60 mt-1">Select a user to view their plan details</p>
              </div>
              
              {/* Search Bar */}
              <SearchInput
                placeholder="Search by email"
                value={searchTerm}
                onChange={setSearchTerm}
                onDebouncedChange={setDebouncedSearchTerm}
                showClearButton
                className="w-64 shrink-0"
              />
            </div>
          </div>
          
          {isLoadingUsers ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-dark-invirtus/5 border-t-transparent mb-3"></div>
              <p className="text-dark-invirtus/60 font-medium text-sm">Loading users...</p>
            </div>
          ) : (
            <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-dark-invirtus/60 font-medium text-sm">
                    {debouncedSearchTerm.trim() ? 'No users found matching your search' : 'No users available'}
                  </p>
                </div>
              ) : (
                filteredUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                    selectedUser?.id === user.id
                      ? 'border-gradient-purple bg-gradient-to-r from-purple/10 to-light-purple/20'
                      : 'border-dark-invirtus/5 hover:border-gradient-purple/50 hover:bg-gradient-to-r hover:from-light-lila/10 hover:to-transparent'
                  }`}
                >
                  <div className="font-heading font-bold text-dark-invirtus text-sm mb-1">{user.name}</div>
                  <div className="text-xs text-dark-invirtus/70 mb-2">{user.email}</div>
                  <div className="flex items-center gap-2 text-xs">
                    <Tag intent="secondary" variant="subtle">
                      {user.company ? `Company: ${user.company}` : 'No Company'}
                    </Tag>
                    <Tag intent="info" variant="subtle">
                      Active: {formatLastActivity(user.last_activity)}
                    </Tag>
                  </div>
                </button>
              ))
              )}
            </div>
          )}
        </div>

        {/* User Plan Details */}
        <div className="bg-white rounded-xl border border-light-lila/20 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-light-lila/30 bg-gradient-to-r from-light-blue/20 to-blue/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-dark-invirtus/10 rounded-lg">
                  <Target className="w-4 h-4 text-dark-invirtus" />
                </div>
                <div>
                  <h3 className="text-sm font-heading font-bold text-dark-invirtus">Plan Details</h3>
                  <p className="text-xs text-dark-invirtus/60 mt-0.5">
                    {selectedUser ? `${selectedUser.name || selectedUser.email}'s plan` : 'Select a user to view plan'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {!selectedUser ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="p-4 bg-gradient-to-br from-light-lila/20 to-light-purple/30 rounded-xl mb-4">
                <Users className="w-10 h-10 text-gradient-purple" />
              </div>
              <p className="text-dark-invirtus/60 font-medium text-sm">Select a user from the list to view their plan details</p>
            </div>
          ) : isLoadingPlan ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-dark-invirtus/5 border-t-blue mb-3"></div>
              <p className="text-dark-invirtus/60 font-medium text-sm">Loading plan details...</p>
            </div>
          ) : userPlan?.has_plan ? (
            <div className="p-4 space-y-4">
              {/* Plan Status & Info Section */}
              <div className="rounded-lg p-3 border border-dark-invirtus/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Tag intent={userPlan.expired ? 'error' : 'success'} variant="subtle">
                      {userPlan.expired ? 'Plan Expired' : 'Plan Active'}
                    </Tag>
                  </div>
                  <Tag intent="secondary" variant="subtle">
                    {userPlan.plan_kind === 'fixed' ? 'FIXED PLAN' : 
                     userPlan.plan_kind === 'payg' ? 'PAY-AS-YOU-GO' : 
                     userPlan.plan_kind === 'custom' ? 'CUSTOM PLAN' : 'UNKNOWN'}
                  </Tag>
                </div>
                
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-dark-invirtus/5">
                  <div>
                    <span className="text-xs font-heading font-semibold text-dark-invirtus/60 block mb-0.5">Plan Code</span>
                    <p className="font-heading font-bold text-dark-invirtus text-xs">
                      {userPlan.plan_kind === 'fixed' && userPlan.fixed_plan_code 
                        ? userPlan.fixed_plan_code.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                        : userPlan.plan_kind === 'payg' 
                          ? 'Pay as you go' 
                          : 'Custom Plan'
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-heading font-semibold text-dark-invirtus/60 block mb-0.5 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Expiration
                    </span>
                    {editingExpiration ? (
                      <div className="flex items-center gap-1.5 mt-1">
                        <Input
                          type="date"
                          value={expirationDate}
                          onChange={(e) => setExpirationDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          disabled={isSaving}
                          size="sm"
                          rounded="md"
                        />
                        <Button
                          label="Save"
                          intent="primary"
                          size="sm"
                          onClick={handleSaveExpiration}
                          disabled={isSaving || !expirationDate}
                          loading={updateExpirationMutation.isPending}
                        />
                        <Button
                          label="Cancel"
                          type="outlined"
                          intent="muted"
                          size="sm"
                          onClick={() => setEditingExpiration(false)}
                          disabled={isSaving}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <p className="font-heading font-bold text-dark-invirtus text-xs">
                          {formatPlanExpirationDate(userPlan.expires_at)}
                          {userPlan.expires_at && (
                            <span className="text-sm text-dark-invirtus/80 ml-1.5">
                              - {formatDaysUntilExpiration(userPlan.expires_at)}
                            </span>
                          )}
                        </p>
                        <Button
                          type="icon"
                          intent="muted"
                          size="sm"
                          icon={<Edit/>}
                          onClick={handleEditExpiration}
                          disabled={isSaving}
                          title="Edit expiration date"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Usage Statistics */}
              <div>
                <h4 className="text-xs font-heading font-semibold text-dark-invirtus/70 mb-2 flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-purple" />
                  Usage Statistics
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {USAGE_METRICS.filter(
                    ({ key }) => userPlan.usage[key as keyof typeof userPlan.usage] !== undefined
                  ).map(({ key: metric, label, icon: Icon }, index, shown) => {
                    const used = userPlan.usage[metric as keyof typeof userPlan.usage] ?? 0;
                    const limit = userPlan.limits[metric as keyof typeof userPlan.limits];
                    const remaining = userPlan.remaining[metric as keyof typeof userPlan.remaining];
                    const isUnlimited = limit === -1 || limit === null;
                    const percentage = isUnlimited ? 0 : limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
                    // Odd count: stretch the last tile so the grid stays symmetric.
                    const isLoneLast = shown.length % 2 === 1 && index === shown.length - 1;

                    return (
                      <div
                        key={metric}
                        className={`bg-white rounded-lg p-3 border border-light-lila/30 ${
                          isLoneLast ? 'col-span-2' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <div className="p-1 bg-light-lila/30 rounded">
                              <Icon className="w-3 h-3 text-dark-invirtus" />
                            </div>
                            <span className="text-xs font-heading font-semibold text-dark-invirtus/70">
                              {label}
                            </span>
                          </div>
                        </div>
                        <div className={`text-xl font-heading font-bold ${getUsageColor(used, limit)} mb-1`}>
                          {used} / {isUnlimited ? '∞' : limit}
                        </div>
                        <div className="text-xs text-dark-invirtus/60 font-medium mb-1.5">
                          {isUnlimited ? 'Unlimited capacity' : `${remaining} remaining`}
                        </div>
                        {!isUnlimited && limit > 0 && (
                          <ProgressBar
                            value={percentage}
                            max={100}
                            size="xs"
                            intent={
                              (percentage >= 90
                                ? 'error'
                                : percentage >= 70
                                  ? 'warning'
                                  : 'success') satisfies ProgressBarIntent
                            }
                            aria-label={`${label} usage`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 pt-3 border-t border-light-lila/30">
                <div className="flex gap-2">
                  <Button
                    label="Update Plan"
                    intent="muted"
                    size="sm"
                    className="flex-1"
                    icon={<Edit/>}
                    onClick={() => setShowPlanForm(true)}
                  />
                  <Button
                    label="Delete"
                    intent="danger"
                    size="sm"
                    icon={<Trash2/>}
                    onClick={handleDeletePlan}
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-5">
              {/* Status Message */}
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red/10 rounded-full mb-3">
                  <AlertCircle className="w-8 h-8 text-red" />
                </div>
                <h4 className="text-base font-heading font-bold text-dark-invirtus mb-2">No Plan Assigned</h4>
                <p className="text-sm text-dark-invirtus/60">
                  Assign a plan to enable CV scoring and AI interviews for this user
                </p>
              </div>

              {/* Action Button */}
              <Button
                label="Assign Plan"
                intent="muted"
                size="sm"
                className="w-full"
                onClick={() => setShowPlanForm(true)}
                disabled={isSaving}
              />

              {/* Plan Types Info */}
              <div className="border-t border-light-lila/30 pt-4">
                <div className="text-xs font-semibold text-dark-invirtus/70 mb-3">Available Plan Types:</div>
                <div className="space-y-2 text-xs text-dark-invirtus/60">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple mt-1.5 flex-shrink-0"></div>
                    <div><strong className="text-dark-invirtus">Fixed Plans:</strong> Predefined limits with expiration</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue mt-1.5 flex-shrink-0"></div>
                    <div><strong className="text-dark-invirtus">Pay-As-You-Go:</strong> Unlimited with billing</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-purple mt-1.5 flex-shrink-0"></div>
                    <div><strong className="text-dark-invirtus">Enterprise:</strong> Custom limits and features</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Plan Assignment Modal */}
      <PlanAssignmentModal
        isOpen={showPlanForm}
        onClose={() => setShowPlanForm(false)}
        mode="update_plan"
        currentPlan={userPlan || null}
        userId={selectedUserId || undefined}
        plans={plans}
        onSubmit={handlePlanAssignment}
        isSubmitting={isSaving}
        userName={selectedUser?.name}
        userEmail={selectedUser?.email}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete User Plan"
        message={`Are you sure you want to delete ${selectedUser?.email || 'this user'}'s plan? This action cannot be undone.`}
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setShowDeleteConfirm(false),
            type: 'outlined',
            intent: 'secondary',
            disabled: deletePlanMutation.isPending,
          },
          {
            label: 'Delete',
            onClick: handleDeleteConfirm,
            type: 'contained',
            intent: 'danger',
            loading: deletePlanMutation.isPending,
          },
        ]}
      />
    </div>
  );
};